import Footer from "./Footer/Footer";
import Header from "./Header/Header";
import "../../assets/styles/index.scss";
import HeaderLogged from "./HeaderLogged/HeaderLogged";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import theme from "../../muiTheme";
import { Box, Container, Typography, useMediaQuery } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { logoutGygia } from "../../core/services/gygia/gygia.service";
import { API_ENDPOINTS } from "../../config/endpoints.config";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { t } = useTranslation();
  const isDesktopViewport = useMediaQuery("(min-width:768px)");
  const [validUser, setValidUser] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    const checkUser = async (): Promise<void> => {
      // NOTE: Skip user validation in local environment
      if (window.location.origin.includes("localhost")) {
        setValidUser(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(API_ENDPOINTS.USER_CHECK, {
          method: "GET",
          signal: controller.signal,
        });

        if (response.status === 424) {
          setValidUser(false);
        } else if (response.status === 401) {
          setValidUser(true);
          if (!sessionStorage.getItem("redirected")) {
            sessionStorage.setItem("redirected", "true");
            window.location.href = API_ENDPOINTS.USER_LOGIN;
          }
        } else {
          setValidUser(response.ok);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error al verificar usuario:", error);
          setValidUser(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();

    return () => controller.abort();
  }, []);

  const clearCacheStorage = () => {
    if ("caches" in window) {
      caches.keys().then((keyList) => {
        return Promise.all(keyList.map((key) => caches.delete(key)));
      });
    }
  };

  const clearIndexedDB = () => {
    if (window.indexedDB && window.indexedDB.databases) {
      window.indexedDB.databases().then((databases) => {
        databases.forEach((db) => {
          window.indexedDB.deleteDatabase(db?.name || "");
        });
      });
    }
  };

  const userLogout = async () => {
    localStorage.clear();
    sessionStorage.clear();
    await logoutGygia();
    clearCacheStorage();
    clearIndexedDB();
    clearCacheStorage();
  };

  return (
    <>
      {isLoading ? (
        <LoadingComponentComponent
          isLoading={isLoading}
          opacity={0.5}
          position={isDesktopViewport ? "absolute" : "fixed"}
        />
      ) : validUser === null ? null : validUser ? (
        <>
          <HeaderLogged />
          <main
            style={{
              backgroundColor: theme.palette.grey[50],
            }}
          >
            {children}
            <Outlet />
          </main>
          <Footer validUser={validUser} />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100vh",
          }}
        >
          <Header />
          <main style={{ backgroundColor: theme.palette.grey[50] }}>
            <Container
              sx={{ paddingY: { xs: 5, md: 10 }, textAlign: "center" }}
            >
              <Typography
                variant="h3"
                component="h3"
                sx={{ textAlign: "center" }}
              >
                {t(`Usuario no encontrado`)}
              </Typography>
              <Typography
                variant="body1"
                component="span"
                className="brand-gray"
                sx={{ textAlign: "center" }}
              >
                <p>
                  <Trans i18nKey="Usuario no encontrado mensaje">
                    Link
                    <a
                      className="brand-red"
                      href="mailto:atencionalcliente@totalenergies.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      content
                    </a>
                    <a
                      className="brand-red"
                      href="tel:900926676"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      content
                    </a>
                  </Trans>
                </p>
              </Typography>
              <button
                onClick={userLogout}
                className="button-element button primary"
                style={{ marginTop: 24 }}
              >
                Vuelve a intentarlo
              </button>
            </Container>
          </main>
          <Footer validUser={validUser} />
        </Box>
      )}
    </>
  );
};

export default Layout;
